import { useCallback, useEffect, useState } from "react";
import {
  loadAttendeeReport,
  type AttendeeReportData,
} from "../services/attendeeReportService";

export interface AttendeeReportTarget {
  eventId: string;
  personId: string;
}

interface UseAttendeeReportResult {
  data: AttendeeReportData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * One-shot loader for the per-attendee Event Experience Report (ADR-136).
 * Single person-keyed target; the resolver branches internally on
 * `person.authUid` for claimed-vs-pending semantics.
 *
 * Mirrors the loading/error contract of `useFirestoreCollectionOnce` so
 * call sites and snackbar refresh patterns stay consistent. Per ADR-019,
 * the admin uses `getDocs` over `onSnapshot`; refetch after edits if a
 * row needs to update.
 */
export function useAttendeeReport(
  target: AttendeeReportTarget | null,
): UseAttendeeReportResult {
  const [data, setData] = useState<AttendeeReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!target) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadAttendeeReport(target.eventId, target.personId)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target?.eventId, target?.personId, version]);

  return { data, loading, error, refetch };
}
